import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, unwrap } from "../client";
import { qk } from "../keys";
import { streamSSE } from "../sse";
import type { OperationEvent } from "../types";

export function useOperation(operationId: string | null | undefined) {
  return useQuery({
    queryKey: qk.operation(operationId ?? ""),
    queryFn: () =>
      unwrap(api.GET("/v1/operations/{operation_id}", { params: { path: { operation_id: operationId! } } })),
    enabled: !!operationId,
  });
}

export interface OperationStreamState {
  events: OperationEvent[];
  connected: boolean;
  finished: boolean;
  error: unknown;
}

/**
 * Follow a long-running operation (deploy, build, db branch) over SSE. Replays past events then
 * follows live; resumes with Last-Event-ID; ends after `operation.finished`. Refreshes the
 * operation query on each step so `useOperation` stays in sync.
 */
export function useOperationStream(operationId: string | null | undefined): OperationStreamState {
  const qc = useQueryClient();
  const [events, setEvents] = useState<OperationEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!operationId) return;
    setEvents([]);
    setFinished(false);
    const ac = new AbortController();
    void streamSSE<OperationEvent>(`/v1/operations/${encodeURIComponent(operationId)}/events`, {
      signal: ac.signal,
      onOpen: () => setConnected(true),
      onError: setError,
      isTerminal: (m) => m.event === "operation.finished",
      onMessage: (m) => {
        if (m.event === "heartbeat") return;
        setEvents((prev) => [...prev, m.data]);
        if (m.event.startsWith("step.") || m.event === "operation.finished") {
          void qc.invalidateQueries({ queryKey: qk.operation(operationId) });
        }
        if (m.event === "operation.finished") setFinished(true);
      },
    }).finally(() => setConnected(false));
    return () => ac.abort();
  }, [operationId, qc]);

  return { events, connected, finished, error };
}
